import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faArrowLeft,
  faExternalLinkAlt,
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import ReportCard from './ReportCard';
import ReportRenderer from '../TemplateRenderers/ReportRenderer';

const ReportsContent = ({ category, reportName, reportsData, token }) => {
  const [reportDefinition, setReportDefinition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load specific report definition when reportName is selected
  useEffect(() => {
    if (category && reportName) {
      loadReportDefinition();
    } else {
      setReportDefinition(null);
      setError(null);
    }
  }, [category, reportName]);

  const loadReportDefinition = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/reports/${category}/${reportName}/definition`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReportDefinition(data.definition);
      } else {
        setError(`Failed to load report definition: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error loading report definition:', err);
      setError('Error loading report definition');
    } finally {
      setLoading(false);
    }
  };

  // Show dashboard view (category overview or all reports)
  if (!reportName) {
    const currentReports = category ? reportsData[category] || [] : Object.values(reportsData).flat();
    const totalReports = Object.values(reportsData).reduce((sum, reports) => sum + reports.length, 0);

    return (
      <div className="p-6 overflow-y-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <FontAwesomeIcon icon={faChartBar} className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {category ? getCategoryDisplayName(category) : 'All Reports'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {category 
                  ? `${currentReports.length} reports in ${category} category`
                  : `${totalReports} total reports across all categories`
                }
              </p>
            </div>
          </div>

          {category && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <span>Reports</span>
              <FontAwesomeIcon icon={faArrowLeft} className="mx-2 rotate-180" />
              <span className="font-medium">{getCategoryDisplayName(category)}</span>
            </div>
          )}
        </div>

        {/* Reports Grid */}
        {currentReports.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentReports.map((report) => (
              <ReportCard
                key={`${report.category}-${report.name}`}
                report={report}
                onClick={() => {
                  // Navigate to specific report
                  window.location.href = `/reports/${report.category}/${report.name}`;
                }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FontAwesomeIcon icon={faChartBar} className="text-6xl text-gray-300 dark:text-gray-600 mb-6" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {category ? 'No reports in this category' : 'No reports available'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {category 
                ? `There are no reports in the ${category} category yet.`
                : 'No reports have been created yet.'
              }
            </p>
            <div className="text-sm text-gray-500 dark:text-gray-500">
              <p>To add a new report, create a JavaScript file in:</p>
              <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded mt-1 inline-block">
                /reports/{category || '{category}'}/YourReport.js
              </code>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show specific report
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-500 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Report</h3>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={loadReportDefinition}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!reportDefinition) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-yellow-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Report Not Found</h3>
          <p className="text-gray-600 dark:text-gray-400">
            The requested report could not be loaded.
          </p>
        </div>
      </div>
    );
  }

  // Render report using the existing ReportRenderer
  return (
    <div className="h-full overflow-hidden">
      <ReportRenderer
        definition={reportDefinition}
        agentId="reports" // Use a special agentId for centralized reports
        token={token}
        onClose={() => {
          // Navigate back to category view
          window.location.href = `/reports/${category}`;
        }}
        componentName={reportName}
      />
    </div>
  );
};

const getCategoryDisplayName = (category) => {
  switch (category) {
    case 'system': return 'System Reports';
    case 'business': return 'Business Reports'; 
    case 'custom': return 'Custom Reports';
    default: return category.charAt(0).toUpperCase() + category.slice(1);
  }
};

export default ReportsContent;